type RejectionEventLike = Pick<
  PromiseRejectionEvent,
  "reason" | "preventDefault" | "stopImmediatePropagation"
>;

type EventLike = Pick<
  Event,
  "preventDefault" | "stopImmediatePropagation"
>;

type ErrorTarget = {
  addEventListener: (
    type: string,
    listener: (event: any) => void,
  ) => void;
};

const extensionPrefixes = [
  "chrome-extension://",
  "moz-extension://",
  "safari-extension://",
  "safari-web-extension://",
  "ms-browser-extension://",
  "extension://",
];

const hasExtensionPrefix = (text: string): boolean =>
  extensionPrefixes.some((prefix) => text.includes(prefix));

const extractStrings = (input: unknown): string[] => {
  if (typeof input === "string") return [input];
  if (typeof input !== "object" || input === null) return [];
  const obj = input as Record<string, unknown>;
  const keys = ["message", "stack", "filename", "fileName", "sourceURL"];
  return keys
    .map((k) => obj[k])
    .filter((v): v is string => typeof v === "string");
};

export const isEventRejection = (reason: unknown): boolean => {
  if (!reason || typeof reason !== "object") return false;
  if (reason instanceof Event) return true;
  if (typeof DOMException !== "undefined" && reason instanceof DOMException) {
    return true;
  }
  const tag = Object.prototype.toString.call(reason);
  if (tag.endsWith("Event]") || tag === "[object DOMException]") return true;
  const name = (reason as { constructor?: { name?: string } }).constructor
    ?.name;
  if (
    typeof name === "string" &&
    (name === "Event" || name.endsWith("Event") || name === "DOMException")
  ) {
    return true;
  }
  if (
    "isTrusted" in reason &&
    typeof (reason as { isTrusted: unknown }).isTrusted === "boolean"
  ) {
    return true;
  }
  const message = (reason as { message?: unknown }).message;
  if (typeof message === "string" && message.includes("IDBDatabase")) {
    return true;
  }
  return false;
};

export const isBrowserExtensionError = (reason: unknown): boolean =>
  extractStrings(reason).some(
    (str) =>
      hasExtensionPrefix(str) ||
      str.includes("Object Not Found Matching Id"),
  );

export const isExtensionHostObjectRejection = (reason: unknown): boolean =>
  isBrowserExtensionError(reason);

export const isResizeObserverError = (reason: unknown): boolean =>
  extractStrings(reason).some((str) => str.includes("ResizeObserver"));

export const isNextSiblingNullError = (reason: unknown): boolean =>
  extractStrings(reason).some(
    (str) =>
      str.includes("nextSibling") &&
      (str.includes("null") || str.includes("undefined")),
  );

export const isNullRejection = (reason: unknown): boolean =>
  reason === null || reason === undefined;

export const isScriptError = (event: unknown): boolean =>
  typeof ErrorEvent !== "undefined" &&
  event instanceof ErrorEvent &&
  event.message === "Script error." &&
  !event.error;

export const isNextSiblingNullErrorEvent = (event: unknown): boolean =>
  typeof ErrorEvent !== "undefined" &&
  event instanceof ErrorEvent &&
  (isNextSiblingNullError(event.error) ||
    isNextSiblingNullError(event.message));

export const isBrowserExtensionErrorEvent = (event: unknown): boolean =>
  typeof event === "object" &&
  event !== null &&
  (isBrowserExtensionError((event as { error?: unknown }).error) ||
    isBrowserExtensionError((event as { filename?: unknown }).filename) ||
    isBrowserExtensionError((event as { message?: unknown }).message));

export const isResizeObserverErrorEvent = (event: unknown): boolean =>
  typeof event === "object" &&
  event !== null &&
  (isResizeObserverError((event as { error?: unknown }).error) ||
    isResizeObserverError((event as { message?: unknown }).message));

// Swallows unhandledrejection storms from @instantdb/core's floating _gc()
// promise when IndexedDB fails (https://github.com/instantdb/instant/issues/2795),
// browser extension scripts / host object rejections, lit-html repeat directive
// nextSibling null errors, ResizeObserver loop errors, and null promise rejections.

export const suppressEventRejection = (event: RejectionEventLike): void => {
  if (
    !isEventRejection(event.reason) &&
    !isBrowserExtensionError(event.reason) &&
    !isResizeObserverError(event.reason) &&
    !isNextSiblingNullError(event.reason) &&
    !isNullRejection(event.reason)
  ) {
    return;
  }
  event.preventDefault();
  event.stopImmediatePropagation();
};

export const suppressScriptError = (event: EventLike): void => {
  if (
    !isScriptError(event) &&
    !isNextSiblingNullErrorEvent(event) &&
    !isBrowserExtensionErrorEvent(event) &&
    !isResizeObserverErrorEvent(event)
  ) {
    return;
  }
  event.preventDefault();
  event.stopImmediatePropagation();
};

export const installEventRejectionSuppression = (
  target: ErrorTarget,
): void => {
  target.addEventListener(
    "unhandledrejection",
    (event) => suppressEventRejection(event),
  );
  target.addEventListener(
    "error",
    (event) => suppressScriptError(event),
  );
};
